import React from 'react';
import ChartBase from './ChartBase';
import { getItemValue, getItemLabel, getMaxValue } from './ChartData';
import '../../styles/chart-styles.css';

const LineChart = ({ 
  data, 
  moduleId, 
  dataIndex, 
  title, 
  subtitle,
  showLabels = true, 
  animate = true,
  lineColor = null,
  strokeWidth = 2,
  smooth = false,
  className = '',
  series = null,
  showLegend = true,
  ...props 
}) => {
  return (
    <ChartBase 
      title={title} 
      subtitle={subtitle}
      data={data}
      moduleId={moduleId}
      className={className}
    >
      {({ chartArray, maxValue, dimensions, svgRef }) => {
        // Support multiple series or single series
        const isMultiSeries = series && Array.isArray(series) && series.length > 0;
        const seriesData = isMultiSeries ? series : [{ data: chartArray, color: lineColor, name: 'Series 1' }];
        
        // Extract all points and labels from all series
        const allPoints = seriesData.flatMap(s => 
          (s.data || []).map(item => getItemValue(item))
        );
        const labels = (seriesData[0]?.data || chartArray).map(item => getItemLabel(item));
        const maxPoint = Math.max(...allPoints);
        
        // Use dynamic dimensions with more padding for labels
        const svgWidth = dimensions.width || 100;
        const svgHeight = dimensions.height || 40;
        const padding = { top: 10, right: 10, bottom: 30, left: 40 };
        const chartWidth = svgWidth - padding.left - padding.right;
        const chartHeight = svgHeight - padding.top - padding.bottom;
        
        // Calculate positions based on actual container dimensions
        const firstSeriesPoints = (seriesData[0]?.data || chartArray).map(item => getItemValue(item));
        const xStep = chartWidth / (firstSeriesPoints.length - 1);
        const yScale = chartHeight / maxPoint;
        
        // Generate SVG points for each series
        const seriesSvgPoints = seriesData.map(s => {
          const points = (s.data || []).map(item => getItemValue(item));
          return points.map((p, index) => {
            const x = padding.left + index * xStep;
            const y = padding.top + chartHeight - (p * yScale);
            return `${x},${y}`;
          });
        });

        // Generate y-axis labels
        const yLabels = [];
        const yLabelCount = 5;
        for (let i = 0; i <= yLabelCount; i++) {
          const value = (maxPoint * i) / yLabelCount;
          const y = padding.top + chartHeight - (value * yScale);
          yLabels.push({ value, y });
        }

        return (
          <svg 
            ref={svgRef}
            viewBox={`0 0 ${svgWidth} ${svgHeight}`} 
            className="w-full h-full"
          >
            {/* Grid lines */}
            {yLabels.map((label, index) => (
              <line
                key={`grid-${index}`}
                x1={padding.left}
                y1={label.y}
                x2={svgWidth - padding.right}
                y2={label.y}
                stroke="var(--theme-border)"
                strokeWidth="1"
                strokeDasharray="2,2"
              />
            ))}
            
            {/* Axes */}
            <line
              x1={padding.left}
              y1={padding.top}
              x2={padding.left}
              y2={svgHeight - padding.bottom}
              stroke="var(--theme-border)"
              strokeWidth="2"
            />
            <line
              x1={padding.left}
              y1={svgHeight - padding.bottom}
              x2={svgWidth - padding.right}
              y2={svgHeight - padding.bottom}
              stroke="var(--theme-border)"
              strokeWidth="2"
            />
            
            {/* Y-axis labels */}
            {yLabels.map((label, index) => (
              <text
                key={`y-label-${index}`}
                x={padding.left - 5}
                y={label.y + 4}
                textAnchor="end"
                fontSize="10"
                fill="var(--theme-primary)"
                className="text-xs"
              >
                {label.value.toFixed(1)}
              </text>
            ))}
            
            {/* X-axis labels */}
            {showLabels && labels.map((label, index) => (
              <text
                key={`x-label-${index}`}
                x={padding.left + index * xStep}
                y={svgHeight - padding.bottom + 15}
                textAnchor="middle"
                fontSize="10"
                fill="var(--theme-primary)"
                className="text-xs"
              >
                {label}
              </text>
            ))}
            
            {/* Data lines - multiple series */}
            {seriesSvgPoints.map((svgPoints, seriesIndex) => (
              <polyline
                key={`series-${seriesIndex}`}
                fill="none"
                stroke={seriesData[seriesIndex].color || `hsl(${seriesIndex * 60}, 70%, 50%)`}
                strokeWidth={strokeWidth}
                points={svgPoints.join(' ')}
              />
            ))}

            {/* Legend */}
            {showLegend && isMultiSeries && (
              <g transform={`translate(${padding.left}, ${padding.top - 5})`}>
                {seriesData.map((s, index) => (
                  <g key={`legend-${index}`} transform={`translate(${index * 80}, 0)`}>
                    <line
                      x1={0}
                      y1={0}
                      x2={20}
                      y2={0}
                      stroke={s.color || `hsl(${index * 60}, 70%, 50%)`}
                      strokeWidth={strokeWidth}
                    />
                    <text
                      x={25}
                      y={4}
                      fontSize="10"
                      fill="var(--theme-primary)"
                      className="text-xs"
                    >
                      {s.name || `Series ${index + 1}`}
                    </text>
                  </g>
                ))}
              </g>
            )}
            
            {/* Data points
            {svgPoints.map((point, index) => {
              const [x, y] = point.split(',').map(Number);
              return (
                <circle
                  key={`point-${index}`}
                  cx={x}
                  cy={y}
                  r="3"
                  fill={lineColor || 'var(--theme-primary)'}
                  className="hover:r-4 transition-all"
                />
              );
            })} */}
          </svg>
        );
      }}
    </ChartBase>
  );
};

export default LineChart;
